/**
 * Logger utility for consistent logging across the application
 * Automatically handles development vs production environments
 * In production, errors are sent to error service instead of console
 */

import { errorService } from '@/data/services/error/errorService'

/**
 * Logger object with methods that only log in development
 * In production, errors are sent to error service for monitoring
 */
export const logger = {
  /**
   * Log debug/info messages (only in development)
   * @param {...any} args - Arguments to log
   */
  log: (...args) => {
    if (import.meta.env.DEV) {
      // biome-ignore lint/suspicious/noConsole: logger is the single sanctioned console gateway
      console.log(...args)
    }
  },

  /**
   * Log error messages
   * In development: logs to console
   * In production: sends to error service
   * @param {...any} args - Arguments to log
   */
  error: (...args) => {
    if (import.meta.env.DEV) {
      console.error(...args)
    } else {
      // In production, send to error service for monitoring
      const errorMessage = args
        .map((arg) => {
          if (arg instanceof Error) {
            return arg.message
          }
          if (typeof arg === 'object') {
            try {
              return JSON.stringify(arg)
            } catch {
              return String(arg)
            }
          }
          return String(arg)
        })
        .join(' ')

      errorService.logError(new Error(errorMessage), {
        context: 'Logger',
        source: 'console.error',
      })
    }
  },

  /**
   * Log warning messages (only in development)
   * @param {...any} args - Arguments to log
   */
  warn: (...args) => {
    if (import.meta.env.DEV) {
      console.warn(...args)
    }
  },

  /**
   * Log info messages (only in development)
   * @param {...any} args - Arguments to log
   */
  info: (...args) => {
    if (import.meta.env.DEV) {
      // biome-ignore lint/suspicious/noConsole: logger is the single sanctioned console gateway
      console.info(...args)
    }
  },

  /**
   * Log debug messages (only in development)
   * @param {...any} args - Arguments to log
   */
  debug: (...args) => {
    if (import.meta.env.DEV) {
      // biome-ignore lint/suspicious/noConsole: logger is the single sanctioned console gateway
      console.debug(...args)
    }
  },
}

/**
 * Default export for convenience
 */
